import React, { useState, useMemo, useEffect } from 'react';
import CardCommon from '@/pages/opinion/components/CardCommon';
import FilterBoxCommon from '@/pages/opinion/components/FilterBoxCommon';
import DetailKeyword from '@op/pages/Detail/component/DetailKeyword';
import { GameStoreTable } from '@op/pages/Detail/component/DetailTable';
import { SortBy } from 'tea-component/lib/table/addons';
import { Tag } from 'tea-component';
import { useDispatch, useIntl } from 'umi';
import moment from 'moment';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_TM } from '@op/utils/format';
import Sentiment from '@op/components/Snippets/Sentiment';
import { useFeedsGpt } from '@op/pages/feeds/hooks/useFeedsGpt';
import { SAVE_PAGE_NAME } from '@op/components/GPT/constants';

interface CommentTableSectionProps {
  filter: any;
  filterDetail?: any;
  gameInfo?: any;
  wholeGameInfo?: any;
  chartClickFilter?: {
    time?: string;
    sentiment?: 'positive' | 'negative';
  } | null;
  onClearChartFilter?: () => void;
}

const CommentTableSection: React.FC<CommentTableSectionProps> = ({
  filter,
  gameInfo,
  wholeGameInfo,
  chartClickFilter,
  onClearChartFilter,
}) => {
  const { formatMessage } = useLanguage();
  const [feedsStats, setFeedsStats] = useState({});
  const [feedsStatsLoading, setFeedsStatsLoading] = useState<boolean>(true);
  const [selectKeyword, setSelectKeyword] = useState<string[]>([]);
  const [selectRenderKeyword, setSelectRenderKeyword] = useState<{
    origin: string[];
    zh: string[];
    en: string[];
  }>({ origin: [], zh: [], en: [] });
  const [sortBy, setSortBy] = useState<SortBy>({
    by: 'comment_time',
    order: 'desc',
  });
  const [sortByOptions, setSortByOptions] = useState<
    { text: string; value: string }[]
  >([]);
  const [total, setTotal] = useState(0);

  const mergedFilter = useMemo(() => {
    if (!chartClickFilter) return filter;
    const { time, sentiment } = chartClickFilter;
    const result = { ...filter };
    if (time) {
      result.start_time = moment(time)
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss');
      result.end_time = moment(time).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    }
    if (sentiment) {
      result.reviews_rating = [sentiment];
    }
    return result;
  }, [filter, chartClickFilter]);

  const renderChartFilterTags = () => {
    if (!chartClickFilter) return null;
    const tags: JSX.Element[] = [];
    if (chartClickFilter.time) {
      tags.push(
        <Tag key="time" theme="primary" onClose={onClearChartFilter}>
          {DBO_TM.timeTitle(mergedFilter)}
        </Tag>,
      );
    }
    if (chartClickFilter.sentiment) {
      tags.push(
        <Tag key="sentiment" theme="primary" onClose={onClearChartFilter}>
          <Sentiment
            sentiment={chartClickFilter.sentiment === 'positive' ? 5 : 1}
            showValue={false}
          ></Sentiment>
        </Tag>,
      );
    }
    return (
      <span className="dbo-chart-filter-tags">
        {formatMessage('当前选中')}：{tags}
      </span>
    );
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch({
      type: 'opinionAIShortIdFilter/save',
      payload: { ...filter, [SAVE_PAGE_NAME]: 'SteamPerformance_VersionAnalysis' },
    });
  }, []);

  const commonParams = useMemo(() => {
    return {
      purpose: [],
      sort_item: sortBy.by,
      sort_model: sortBy.order,
      keyword: selectKeyword,
    };
  }, [sortBy, selectKeyword]);

  const { GptBtnElement, GptElement } = useFeedsGpt({
    filter,
    commonParams,
    gameInfo,
    specialParams: {},
    match_model: 'exact',
    wholeGameInfo,
    gptName: 'Version_Analysis_Details',
    contentCard: true,
    feedsStats,
  });

  return (
    <CardCommon loadingHeight={300}>
      {renderChartFilterTags()}
      <DetailKeyword
        setSelectRenderKeyword={setSelectRenderKeyword}
        filter={mergedFilter}
        selectKeyword={selectKeyword}
        setSelectKeyword={setSelectKeyword}
        feedsStatsLoading={feedsStatsLoading}
        setFeedsStatsLoading={setFeedsStatsLoading}
        contentCard
        tableTotal={total}
        backCommentTotal={(value: number) => setTotal(value)}
        GptBtnElement={GptBtnElement}
        handleStatsData={(stats) => setFeedsStats(stats)}
      />
      {GptElement}
      <FilterBoxCommon
        className="dbo-stats-selectbox"
        left={[
          {
            key: 'sortBy',
            defaultValue: sortBy.by,
            defaultParams: {
              options: sortByOptions,
              onChange: (key: string) => setSortBy({ order: 'desc', by: key }),
            },
          },
        ]}
        onChange={() => { }}
      />
      <GameStoreTable
        setSortBy={setSortBy}
        sortBy={sortBy}
        renderKeyword={selectRenderKeyword}
        channelType="comments"
        filter={mergedFilter}
        selectKeyword={selectKeyword}
        backLoading={() => { }}
        gameInfo={gameInfo}
        wholeGameInfo={wholeGameInfo}
        setSortByOptions={setSortByOptions}
      />
    </CardCommon>
  );
};

export default CommentTableSection;
